import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Calendar, AlertCircle, MessageSquare, Target, CheckCircle2, Loader2, DollarSign, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { EODFormData, LEAD_QUALITY_OPTIONS, EODSubmission } from '@/types/eod';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface EODFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EODFormData) => EODSubmission;
  onSkip: () => void;
  existingSubmission?: EODSubmission | null;
  isEditing?: boolean;
  saveDraft: (data: Partial<EODFormData>) => void;
  loadDraft: () => Partial<EODFormData> | null;
  clearDraft: () => void;
}

const initialFormData: EODFormData = {
  demos_booked: '',
  demos_showed: '',
  demos_closed: '',
  revenue_closed: '',
  lead_quality: '',
  lead_quality_issues: '',
  issues: '',
  notes: '',
  tomorrow_focus: '',
};

export function EODFormModal({
  isOpen,
  onClose,
  onSubmit,
  onSkip,
  existingSubmission,
  isEditing,
  saveDraft,
  loadDraft,
  clearDraft
}: EODFormModalProps) {
  const [formData, setFormData] = useState<EODFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof EODFormData, string>>>({});
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showZeroDemosConfirm, setShowZeroDemosConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState<EODSubmission | null>(null);

  // Load draft or existing submission on mount
  useEffect(() => {
    if (isOpen) {
      if (existingSubmission && isEditing) {
        setFormData({
          demos_booked: existingSubmission.demos_booked.toString(),
          demos_showed: existingSubmission.demos_showed.toString(),
          demos_closed: existingSubmission.demos_closed.toString(),
          revenue_closed: existingSubmission.revenue_closed?.toString() || '',
          lead_quality: existingSubmission.lead_quality,
          lead_quality_issues: existingSubmission.lead_quality_issues || '',
          issues: existingSubmission.issues,
          notes: existingSubmission.notes,
          tomorrow_focus: existingSubmission.tomorrow_focus,
        });
      } else {
        const draft = loadDraft();
        if (draft) {
          setFormData(prev => ({ ...prev, ...draft }));
        }
      }
    }
  }, [isOpen, existingSubmission, isEditing, loadDraft]);

  // Auto-save draft on form changes
  useEffect(() => {
    if (isOpen && !isEditing) {
      const hasContent = Object.values(formData).some(v => v !== '');
      if (hasContent) {
        saveDraft(formData);
      }
    }
  }, [formData, isOpen, isEditing, saveDraft]);

  const handleChange = useCallback((field: keyof EODFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const validate = useCallback(() => {
    const newErrors: Partial<Record<keyof EODFormData, string>> = {};

    if (formData.demos_booked === '') {
      newErrors.demos_booked = 'Please enter demos booked';
    } else if (parseInt(formData.demos_booked) < 0) {
      newErrors.demos_booked = 'Cannot be negative';
    }

    if (formData.demos_showed === '') {
      newErrors.demos_showed = 'Please enter demos that showed';
    } else {
      const booked = parseInt(formData.demos_booked) || 0;
      const showed = parseInt(formData.demos_showed) || 0;
      if (showed > booked) {
        newErrors.demos_showed = 'Cannot exceed demos booked';
      }
      if (showed < 0) {
        newErrors.demos_showed = 'Cannot be negative';
      }
    }

    if (formData.demos_closed === '') {
      newErrors.demos_closed = 'Please enter demos closed';
    } else {
      const showed = parseInt(formData.demos_showed) || 0;
      const closed = parseInt(formData.demos_closed) || 0;
      if (closed > showed) {
        newErrors.demos_closed = 'Cannot exceed demos that showed';
      }
      if (closed < 0) {
        newErrors.demos_closed = 'Cannot be negative';
      }
    }

    if (!formData.lead_quality) {
      newErrors.lead_quality = 'Please select lead quality';
    }

    // Require explanation if Fair or Poor
    if ((formData.lead_quality === 'fair' || formData.lead_quality === 'poor') && !formData.lead_quality_issues.trim()) {
      newErrors.lead_quality_issues = 'Please explain the quality issues';
    }

    // Require issues explanation if 0 demos booked
    const demosBooked = parseInt(formData.demos_booked) || 0;
    if (demosBooked === 0 && !formData.issues.trim()) {
      newErrors.issues = 'Please explain why no demos were booked';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    // Check for 0 demos confirmation
    const demosBooked = parseInt(formData.demos_booked) || 0;
    if (demosBooked === 0 && !showZeroDemosConfirm) {
      setShowZeroDemosConfirm(true);
      return;
    }

    setIsSubmitting(true);

    const submission = onSubmit(formData);
    setSubmittedData(submission);
    setIsSubmitting(false);
    setShowSuccess(true);
    clearDraft();

    // Auto-close after 3 seconds
    setTimeout(() => {
      setShowSuccess(false);
      setFormData(initialFormData);
      onClose();
    }, 3000);
  }, [formData, validate, onSubmit, onClose, clearDraft, showZeroDemosConfirm]);

  const handleSkip = useCallback(() => {
    setShowSkipConfirm(true);
  }, []);

  const confirmSkip = useCallback(() => {
    setShowSkipConfirm(false);
    onSkip();
  }, [onSkip]);

  const handleCloseAttempt = useCallback(() => {
    const hasContent = Object.values(formData).some(v => v !== '');
    if (hasContent) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  }, [formData, onClose]);

  const confirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    setFormData(initialFormData);
    onClose();
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleCloseAttempt();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleCloseAttempt]);

  const showRate = (() => {
    const booked = parseInt(formData.demos_booked) || 0;
    const showed = parseInt(formData.demos_showed) || 0;
    if (booked > 0) {
      return Math.round((showed / booked) * 100);
    }
    return null;
  })();

  const closeRate = (() => {
    const showed = parseInt(formData.demos_showed) || 0;
    const closed = parseInt(formData.demos_closed) || 0;
    if (showed > 0) {
      return Math.round((closed / showed) * 100);
    }
    return null;
  })();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={handleCloseAttempt}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-[800px] max-h-[90vh] m-4 overflow-hidden rounded-lg border border-secondary/30 bg-card/95 backdrop-blur-md shadow-2xl"
          style={{
            boxShadow: '0 0 40px hsl(182 52% 58% / 0.15), 0 0 80px hsl(182 52% 58% / 0.08)'
          }}
        >
          {/* Close button */}
          <button
            onClick={handleCloseAttempt}
            className="absolute right-4 top-4 p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-destructive z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {showSuccess ? (
            <SuccessState submission={submittedData!} onViewDashboard={onClose} />
          ) : (
            <div className="overflow-y-auto max-h-[90vh] p-6 md:p-8">
              {/* Header */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-secondary" />
                  {isEditing ? 'Update Today\'s Submission' : 'End of Day Update'}
                </h2>
                <p className="text-muted-foreground mt-1">
                  {format(new Date(), 'EEEE, MMMM d, yyyy')}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-6">
                {/* Demos Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Demos Booked */}
                  <div className="space-y-2">
                    <Label htmlFor="demos_booked" className="flex items-center gap-2 text-foreground">
                      <Users className="w-4 h-4 text-secondary" />
                      Demos Booked <span className="text-destructive">*</span>
                    </Label>
                    <input
                      id="demos_booked"
                      type="number"
                      min="0"
                      placeholder="How many demos booked?"
                      value={formData.demos_booked}
                      onChange={(e) => handleChange('demos_booked', e.target.value)}
                      className="w-full px-4 py-3 bg-surface border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all"
                    />
                    {errors.demos_booked && <p className="text-sm text-destructive">{errors.demos_booked}</p>}
                  </div>

                  {/* Demos Showed */}
                  <div className="space-y-2">
                    <Label htmlFor="demos_showed" className="flex items-center gap-2 text-foreground">
                      <Users className="w-4 h-4 text-secondary" />
                      Demos Showed <span className="text-destructive">*</span>
                    </Label>
                    <input
                      id="demos_showed"
                      type="number"
                      min="0"
                      placeholder="How many showed up?"
                      value={formData.demos_showed}
                      onChange={(e) => handleChange('demos_showed', e.target.value)}
                      className="w-full px-4 py-3 bg-surface border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all"
                    />
                    {errors.demos_showed && <p className="text-sm text-destructive">{errors.demos_showed}</p>}
                    {showRate !== null && (
                      <p className="text-sm text-secondary">
                        Show rate: {showRate}%
                      </p>
                    )}
                  </div>

                  {/* Demos Closed */}
                  <div className="space-y-2">
                    <Label htmlFor="demos_closed" className="flex items-center gap-2 text-foreground">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      Demos Closed <span className="text-destructive">*</span>
                    </Label>
                    <input
                      id="demos_closed"
                      type="number"
                      min="0"
                      placeholder="How many closed?"
                      value={formData.demos_closed}
                      onChange={(e) => handleChange('demos_closed', e.target.value)}
                      className="w-full px-4 py-3 bg-surface border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all"
                    />
                    {errors.demos_closed && <p className="text-sm text-destructive">{errors.demos_closed}</p>}
                    {closeRate !== null && (
                      <p className="text-sm text-green-400">
                        Close rate: {closeRate}%
                      </p>
                    )}
                  </div>
                </div>

                {/* Revenue Closed (Optional) */}
                <div className="space-y-2">
                  <Label htmlFor="revenue_closed" className="flex items-center gap-2 text-foreground">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    Revenue Closed Today <span className="text-muted-foreground text-xs">(Optional)</span>
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <input
                      id="revenue_closed"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="If known, enter revenue amount"
                      value={formData.revenue_closed}
                      onChange={(e) => handleChange('revenue_closed', e.target.value)}
                      className="w-full pl-8 pr-4 py-3 bg-surface border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Live Metrics */}
                {(showRate !== null || closeRate !== null) && (
                  <div className="p-4 rounded-lg border border-border bg-surface/50 space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Live Metrics</h4>
                    <div className="flex flex-wrap gap-4">
                      {showRate !== null && (
                        <div className="flex items-center gap-2">
                          <span className={`text-lg font-semibold ${showRate >= 70 ? 'text-success' : showRate >= 50 ? 'text-warning' : 'text-destructive'}`}>
                            {showRate}%
                          </span>
                          <span className="text-sm text-muted-foreground">show rate</span>
                        </div>
                      )}
                      {closeRate !== null && (
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-semibold text-green-400">
                            {closeRate}%
                          </span>
                          <span className="text-sm text-muted-foreground">close rate</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Lead Quality */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2 text-foreground">
                    Lead Quality <span className="text-destructive">*</span>
                  </Label>
                  <RadioGroup
                    value={formData.lead_quality}
                    onValueChange={(value) => handleChange('lead_quality', value)}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  >
                    {LEAD_QUALITY_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                          formData.lead_quality === option.value
                            ? 'border-secondary bg-secondary/10'
                            : 'border-border bg-surface hover:border-muted-foreground'
                        }`}
                        onClick={() => handleChange('lead_quality', option.value)}
                      >
                        <RadioGroupItem value={option.value} id={option.value} />
                        <Label htmlFor={option.value} className="cursor-pointer flex-1 flex items-center gap-2">
                          <span>{option.emoji}</span>
                          <span className="text-sm">{option.label}</span>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {errors.lead_quality && <p className="text-sm text-destructive">{errors.lead_quality}</p>}
                </div>

                {/* Lead Quality Issues (shown if Fair or Poor) */}
                {(formData.lead_quality === 'fair' || formData.lead_quality === 'poor') && (
                  <div className="space-y-2">
                    <Label htmlFor="lead_quality_issues" className="flex items-center gap-2 text-foreground">
                      <AlertCircle className="w-4 h-4 text-warning" />
                      What was the issue? <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      id="lead_quality_issues"
                      placeholder="Describe the quality issues..."
                      value={formData.lead_quality_issues}
                      onChange={(e) => handleChange('lead_quality_issues', e.target.value)}
                      maxLength={500}
                      className="min-h-[80px] bg-surface border-border focus:border-secondary focus:ring-secondary/20"
                    />
                    {errors.lead_quality_issues && <p className="text-sm text-destructive">{errors.lead_quality_issues}</p>}
                  </div>
                )}

                {/* Issues */}
                <div className="space-y-2">
                  <Label htmlFor="issues" className="flex items-center gap-2 text-foreground">
                    <AlertCircle className="w-4 h-4 text-warning" />
                    Any Issues Today?
                    {parseInt(formData.demos_booked) === 0 && <span className="text-destructive">*</span>}
                  </Label>
                  <Textarea
                    id="issues"
                    placeholder="Lead quality, no-shows, technical issues, etc."
                    value={formData.issues}
                    onChange={(e) => handleChange('issues', e.target.value)}
                    maxLength={500}
                    className="min-h-[80px] bg-surface border-border focus:border-secondary focus:ring-secondary/20"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    {errors.issues && <span className="text-destructive">{errors.issues}</span>}
                    <span className="ml-auto">{formData.issues.length}/500</span>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes" className="flex items-center gap-2 text-foreground">
                    <MessageSquare className="w-4 h-4 text-secondary" />
                    Notes for Venture Hills Team
                  </Label>
                  <Textarea
                    id="notes"
                    placeholder="Anything we should know or adjust?"
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    maxLength={500}
                    className="min-h-[80px] bg-surface border-border focus:border-secondary focus:ring-secondary/20"
                  />
                  <p className="text-xs text-muted-foreground text-right">{formData.notes.length}/500</p>
                </div>

                {/* Tomorrow's Focus */}
                <div className="space-y-2">
                  <Label htmlFor="tomorrow_focus" className="flex items-center gap-2 text-foreground">
                    <Target className="w-4 h-4 text-primary" />
                    Tomorrow's Focus
                  </Label>
                  <Textarea
                    id="tomorrow_focus"
                    placeholder="What are you prioritizing tomorrow?"
                    value={formData.tomorrow_focus}
                    onChange={(e) => handleChange('tomorrow_focus', e.target.value)}
                    maxLength={300}
                    className="min-h-[60px] bg-surface border-border focus:border-secondary focus:ring-secondary/20"
                  />
                  <p className="text-xs text-muted-foreground text-right">{formData.tomorrow_focus.length}/300</p>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
                  <Button
                    type="submit"
                    variant="gradient"
                    size="lg"
                    className="flex-1"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      isEditing ? 'Update Submission' : 'Submit Daily Update'
                    )}
                  </Button>
                  {!isEditing && (
                    <Button
                      type="button"
                      variant="ghost-border"
                      size="lg"
                      onClick={handleSkip}
                    >
                      Skip Today
                    </Button>
                  )}
                </div>

                {/* Link to past reports */}
                <div className="text-center">
                  <a href="/eod-reports" className="text-sm text-secondary hover:text-secondary/80 underline-offset-4 hover:underline">
                    View Past EOD Reports
                  </a>
                </div>
              </form>
            </div>
          )}
        </motion.div>

        {/* Skip Confirmation Dialog */}
        <AlertDialog open={showSkipConfirm} onOpenChange={setShowSkipConfirm}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Skip Today's Update?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure? This helps us serve you better and track your progress.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmSkip} className="bg-warning text-warning-foreground hover:bg-warning/90">
                Yes, Skip Today
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Close Confirmation Dialog */}
        <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Discard Changes?</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes. Your progress will be saved as a draft.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep Editing</AlertDialogCancel>
              <AlertDialogAction onClick={confirmClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Discard
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Zero Demos Confirmation Dialog */}
        <AlertDialog open={showZeroDemosConfirm} onOpenChange={setShowZeroDemosConfirm}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm: 0 Demos Booked</AlertDialogTitle>
              <AlertDialogDescription>
                You're submitting that no demos were booked today. Is this correct?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Go Back</AlertDialogCancel>
              <AlertDialogAction onClick={() => { setShowZeroDemosConfirm(false); handleSubmit(); }}>
                Yes, Confirm
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AnimatePresence>
  );
}

function SuccessState({ submission, onViewDashboard }: { submission: EODSubmission; onViewDashboard: () => void }) {
  const closeRate = submission.demos_showed > 0 
    ? Math.round((submission.demos_closed / submission.demos_showed) * 100) 
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-8 md:p-12 text-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        className="w-20 h-20 mx-auto mb-6 rounded-full bg-success/20 flex items-center justify-center"
      >
        <CheckCircle2 className="w-10 h-10 text-success" />
      </motion.div>

      <h3 className="text-2xl font-bold text-foreground mb-2">
        ✅ Thanks! Update submitted.
      </h3>
      <p className="text-muted-foreground mb-6">
        {format(new Date(submission.submitted_at), 'EEEE, MMMM d, yyyy \'at\' h:mm a')}
      </p>

      <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto mb-8">
        <div className="p-4 rounded-lg bg-surface border border-border">
          <p className="text-sm text-muted-foreground">Show Rate</p>
          <p className={`text-2xl font-bold ${submission.show_rate >= 70 ? 'text-success' : submission.show_rate >= 50 ? 'text-warning' : 'text-destructive'}`}>
            {submission.demos_showed}/{submission.demos_booked} = {submission.show_rate}%
          </p>
        </div>
        <div className="p-4 rounded-lg bg-surface border border-border">
          <p className="text-sm text-muted-foreground">Close Rate</p>
          <p className="text-2xl font-bold text-green-400">
            {submission.demos_closed}/{submission.demos_showed} = {closeRate}%
          </p>
        </div>
      </div>

      <Button variant="gradient" size="lg" onClick={onViewDashboard}>
        View Dashboard
      </Button>

      <p className="text-xs text-muted-foreground mt-4">
        This window will close automatically...
      </p>
    </motion.div>
  );
}
